import { useEffect, useState } from "react";
import api, { formatApiError, formatDate, formatEuro } from "@/lib/api";
import { toast } from "sonner";
import { Plus, X, Trash2 } from "lucide-react";

const TYPES = [
  { key: "particulier", label: "Particulier" },
  { key: "expertise", label: "Cabinet d'expertise" },
  { key: "professionnel", label: "Professionnel" },
];

const EMPTY = {
  type: "particulier",
  nom: "",
  email: "",
  telephone: "",
  adresse: "",
  code_postal: "",
  ville: "",
  raison_sociale: "",
  siret: "",
  contact_compagnie: "",
  notes: "",
};

export default function AdminClients() {
  const [items, setItems] = useState([]);
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState(EMPTY);
  const [drawer, setDrawer] = useState(false);
  const [editing, setEditing] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/clients");
      setItems(data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const openSelected = async (id) => {
    try {
      const { data } = await api.get(`/admin/clients/${id}`);
      setSelected(data);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const openNew = () => {
    setForm(EMPTY);
    setEditing(null);
    setDrawer(true);
  };

  const openEdit = (c) => {
    setForm({ ...EMPTY, ...c });
    setEditing(c.id);
    setDrawer(true);
  };

  const submitForm = async (e) => {
    e.preventDefault();
    try {
      if (editing) {
        await api.patch(`/admin/clients/${editing}`, form);
        toast.success("Client mis à jour");
      } else {
        await api.post("/admin/clients", form);
        toast.success("Client créé");
      }
      setDrawer(false);
      await load();
      if (selected && editing === selected.id) await openSelected(editing);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const remove = async (id) => {
    if (!confirm("Supprimer ce client ?")) return;
    try {
      await api.delete(`/admin/clients/${id}`);
      setItems((s) => s.filter((c) => c.id !== id));
      if (selected?.id === id) setSelected(null);
      toast.success("Client supprimé");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  return (
    <div data-testid="admin-clients-page">
      <div className="flex items-end justify-between mb-8">
        <div>
          <p className="text-eyebrow mb-3">Clients</p>
          <h1 className="h-display text-4xl md:text-5xl">Carnet d'adresses.</h1>
        </div>
        <button onClick={openNew} className="btn-terra" data-testid="clients-new">
          <Plus size={14} /> Nouveau client
        </button>
      </div>

      <div className="grid md:grid-cols-12 gap-8">
        <div className="md:col-span-7">
          {loading ? (
            <p className="text-serial">Chargement</p>
          ) : items.length === 0 ? (
            <p className="text-muted-foreground text-[14px]">Aucun client pour le moment.</p>
          ) : (
            <ul className="border-t border-border" data-testid="clients-list">
              {items.map((c) => (
                <li key={c.id}>
                  <button
                    onClick={() => openSelected(c.id)}
                    className={`w-full text-left py-4 px-3 border-b border-border transition-colors ${
                      selected?.id === c.id ? "bg-muted/60" : "hover:bg-muted/30"
                    }`}
                    data-testid={`client-row-${c.id}`}
                  >
                    <div className="flex items-start justify-between gap-4">
                      <div>
                        <p className="font-serif text-xl leading-tight">{c.nom}</p>
                        {c.raison_sociale && <p className="text-[12.5px] text-muted-foreground mt-1">{c.raison_sociale}</p>}
                        <p className="text-serial mt-2">
                          {c.type} · {formatDate(c.created_at)}
                        </p>
                      </div>
                      {c.email && <span className="text-[12px] text-muted-foreground shrink-0">{c.email}</span>}
                    </div>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>

        <aside className="md:col-span-5 md:sticky md:top-8 self-start">
          {selected ? (
            <div className="border border-border p-6 space-y-5" data-testid="client-detail">
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-serial">{selected.type}</p>
                  <p className="font-serif text-2xl mt-2 leading-tight">{selected.nom}</p>
                  {selected.raison_sociale && <p className="text-[13px] text-muted-foreground mt-1">{selected.raison_sociale}</p>}
                </div>
                <div className="flex gap-2">
                  <button onClick={() => openEdit(selected)} className="text-eyebrow link-underline-static" data-testid="client-edit">
                    Éditer
                  </button>
                  <button onClick={() => remove(selected.id)} className="text-muted-foreground hover:text-destructive ml-2" data-testid="client-delete">
                    <Trash2 size={15} />
                  </button>
                </div>
              </div>
              <div className="space-y-1.5 text-[13.5px]">
                {selected.email && <p>{selected.email}</p>}
                {selected.telephone && <p>{selected.telephone}</p>}
                {selected.adresse && <p className="text-muted-foreground">{selected.adresse}</p>}
                {(selected.code_postal || selected.ville) && (
                  <p className="text-muted-foreground">{[selected.code_postal, selected.ville].filter(Boolean).join(" ")}</p>
                )}
                {selected.siret && <p className="text-muted-foreground">SIRET {selected.siret}</p>}
              </div>

              {selected.devis?.length > 0 && (
                <div className="border-t border-border pt-4">
                  <p className="text-eyebrow mb-3">Devis ({selected.devis.length})</p>
                  <ul className="space-y-2">
                    {selected.devis.map((d) => (
                      <li key={d.id} className="flex justify-between text-[13px]">
                        <span>
                          {d.numero} · {d.titre}
                        </span>
                        <span className="font-medium">{formatEuro(d.total_ttc)}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              {selected.factures?.length > 0 && (
                <div className="border-t border-border pt-4">
                  <p className="text-eyebrow mb-3">Factures ({selected.factures.length})</p>
                  <ul className="space-y-2">
                    {selected.factures.map((f) => (
                      <li key={f.id} className="flex justify-between text-[13px]">
                        <span>
                          {f.numero} · {f.type}
                        </span>
                        <span className="font-medium">{formatEuro(f.montant_ttc)}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          ) : (
            <div className="border border-dashed border-border p-8 text-center text-muted-foreground text-[14px]">
              Sélectionnez un client pour voir l'historique.
            </div>
          )}
        </aside>
      </div>

      {drawer && (
        <div className="fixed inset-0 z-50 flex" data-testid="client-drawer">
          <div className="flex-1 bg-foreground/40" onClick={() => setDrawer(false)} />
          <form
            onSubmit={submitForm}
            className="w-full max-w-[480px] bg-background h-full overflow-y-auto p-8 border-l border-border"
          >
            <div className="flex justify-between items-start mb-8">
              <div>
                <p className="text-eyebrow mb-2">{editing ? "Édition" : "Nouveau"}</p>
                <h2 className="h-display text-3xl">Client</h2>
              </div>
              <button type="button" onClick={() => setDrawer(false)} className="text-muted-foreground hover:text-foreground" data-testid="client-drawer-close">
                <X size={20} />
              </button>
            </div>
            <div className="space-y-5">
              <SelectField label="Type" value={form.type} onChange={(v) => setForm({ ...form, type: v })} options={TYPES} testId="client-type" />
              <Input label="Nom *" value={form.nom} onChange={(v) => setForm({ ...form, nom: v })} required testId="client-nom" />
              <Input label="Email" type="email" value={form.email} onChange={(v) => setForm({ ...form, email: v })} testId="client-email" />
              <Input label="Téléphone" value={form.telephone} onChange={(v) => setForm({ ...form, telephone: v })} testId="client-telephone" />
              <Input label="Adresse" value={form.adresse} onChange={(v) => setForm({ ...form, adresse: v })} testId="client-adresse" />
              <div className="grid grid-cols-2 gap-3">
                <Input label="Code postal" value={form.code_postal} onChange={(v) => setForm({ ...form, code_postal: v })} testId="client-cp" />
                <Input label="Ville" value={form.ville} onChange={(v) => setForm({ ...form, ville: v })} testId="client-ville" />
              </div>
              {form.type !== "particulier" && (
                <>
                  <Input label="Raison sociale" value={form.raison_sociale} onChange={(v) => setForm({ ...form, raison_sociale: v })} testId="client-rs" />
                  <Input label="SIRET" value={form.siret} onChange={(v) => setForm({ ...form, siret: v })} testId="client-siret" />
                </>
              )}
              <div>
                <label className="text-eyebrow block mb-2">Notes</label>
                <textarea
                  className="w-full bg-transparent border border-border p-3 text-[14px] min-h-[80px]"
                  value={form.notes || ""}
                  onChange={(e) => setForm({ ...form, notes: e.target.value })}
                  data-testid="client-notes"
                />
              </div>
            </div>
            <button type="submit" className="btn-terra w-full justify-center mt-8" data-testid="client-save">
              {editing ? "Mettre à jour" : "Créer le client"}
            </button>
          </form>
        </div>
      )}
    </div>
  );
}

function Input({ label, value, onChange, type = "text", required, testId }) {
  return (
    <div>
      <label className="text-eyebrow block mb-2">{label}</label>
      <input
        type={type}
        required={required}
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        className="w-full bg-transparent border-b border-border focus:border-primary py-2.5 text-[14.5px] outline-none transition-colors"
        data-testid={testId}
      />
    </div>
  );
}

function SelectField({ label, value, onChange, options, testId }) {
  return (
    <div>
      <label className="text-eyebrow block mb-2">{label}</label>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full bg-transparent border-b border-border focus:border-primary py-2.5 text-[14.5px] outline-none transition-colors"
        data-testid={testId}
      >
        {options.map((o) => (
          <option key={o.key} value={o.key}>
            {o.label}
          </option>
        ))}
      </select>
    </div>
  );
}
